import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  ModalFooter,
  Label,
} from "reactstrap";
// core components
import Header from "components/Headers/Header";
import { API_GETZIPCODE, API_ADDCUSTOMER } from "../../config/api";
import { api } from "../../config/middleware";
import { useSessionStore } from "../../stores/sessionStore";
import Swal from "sweetalert2";

const AddCustomer = () => {
  const orgId = useSessionStore((s) => s.profile?.orgId);
  const tnId = useSessionStore((s) => s.profile?.tnId);

  const navigate = useNavigate();
  const { user } = useAuth();
  const role = (
    user?.role ||
    localStorage.getItem("role") ||
    "user"
  ).toLowerCase();

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(""); // data url / object url

  const onLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    const objectUrl = URL.createObjectURL(file);
    setLogoPreview(objectUrl);
  };

  // cleanup object URL เมื่อเปลี่ยนไฟล์หรือ unmount
  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
  }, [logoPreview]);

  const [form, setForm] = useState({
    customerType: "บุคคลธรรมดา",
    businessType: "",

    // ข้อมูลลูกค้า (CM*)
    cmName: "", // CMNAME
    cmTaxId: "", // CMTAXID
    cmMobile: "", // CMMOBILE
    cmEmail: "", // CMEMAIL
    cmAddr: "", // CMADDR
    cmAddr2: "", // CMADDR2
    subdistrict: "", // CMSUBDISTRICT
    district: "", // CMDISTRICT
    province: "", // CMPROVINCE
    postalCode: "", // CMZIPCODE

    // ข้อมูลออกใบกำกับ/ที่อยู่ใบเสร็จ (CMBILL*)
    cmBillNameTH: "", // CMBILLNAMETH
    cmBillNameEN: "", // CMBILLNAMEEN
    billAddr: "", // CMBILLADDR
    billAddr2: "", // CMBILLADDR2
    billSubdistrict: "", // CMBILLSUBDISTRICT
    billDistrict: "", // CMBILLDISTRICT
    billProvince: "", // CMBILLPROVINCE
    billZipcode: "", // CMBILLZIPCODE
    billMobile: "", // CMBILLMOBILE
    billEmail: "", // CMBILLEMAIL

    // ผู้ติดต่อ
    contactName: "", // CMCONTACTNAME
    contactMobile: "", // CMCONTACTMOBILE
  });

  // --- ZIP search states ---
  const [zipQuery, setZipQuery] = useState(""); // ค่าที่พิมพ์ในช่อง ZIP
  const [zipList, setZipList] = useState([]); // รายการผลลัพธ์จาก API
  const [zipOpen, setZipOpen] = useState(false); // แสดง/ซ่อน dropdown
  const [zipLoading, setZipLoading] = useState(false);
  const zipBoxRef = useRef(null); // ใช้ปิด dropdown เมื่อคลิกนอกกล่อง

  // ปิด dropdown เมื่อคลิกนอก
  useEffect(() => {
    const onDocClick = (e) => {
      if (!zipBoxRef.current) return;
      if (!zipBoxRef.current.contains(e.target)) setZipOpen(false);
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  // รายการประเภทธุรกิจ (CBID)
  const [businessTypeOptions, setBusinessTypeOptions] = useState([]);
  const [loadingBusinessType, setLoadingBusinessType] = useState(false);

  useEffect(() => {
    const orgId = 1;
    setLoadingBusinessType(true);

    api
      .get(`/Customer/GetBusinessType`, { params: { ORGID: orgId } })
      .then((res) => {
        const rows = res?.data?.data ?? res?.data ?? [];
        setBusinessTypeOptions(rows);
      })
      .catch((err) => {
        console.error("โหลดประเภทธุรกิจล้มเหลว:", err);
        setBusinessTypeOptions([]);
      })
      .finally(() => setLoadingBusinessType(false));
  }, []);

  // เรียก API ทุกครั้งที่พิมพ์รหัสไปรษณีย์ (หน่วงนิดเดียวกัน request ถี่ๆ)
  useEffect(() => {
    // ว่าง = ปิด dropdown และล้างผลลัพธ์
    if (zipQuery === "") {
      setZipList([]);
      setZipOpen(false);
      return;
    }

    // debounce 250ms กันยิงถี่ตอนพิมพ์
    const delay = setTimeout(() => {
      setZipLoading(true);
      const current = zipQuery; // จดค่าที่ใช้ยิงรอบนี้

      api
        .get(API_GETZIPCODE, { params: { zipcode: current } })
        .then((res) => {
          // ถ้าระหว่างรอผู้ใช้พิมพ์ต่อ ให้เมินผลลัพธ์เก่า
          if (current !== zipQuery) return;

          const rows = res?.data?.data ?? [];
          setZipList(rows);
          setZipOpen(true);
        })
        .catch((err) => {
          if (current !== zipQuery) return;
          setZipList([]);
          setZipOpen(false);
        })
        .finally(() => {
          if (current !== zipQuery) return;
          setZipLoading(false);
        });
    }, 250);

    return () => clearTimeout(delay);
  }, [zipQuery]);

  // เมื่อพิมพ์ ZIP
  const onZipChange = (e) => {
    const v = e.target.value;
    setForm((s) => ({ ...s, postalCode: v }));
    setZipQuery(v);
  };

  // เมื่อเลือกผลลัพธ์ใน dropdown
  const onPickZip = (item) => {
    setForm((s) => ({
      ...s,
      postalCode: String(item.zip_code ?? ""),
      subdistrict: item.subdistrict_name ?? "",
      district: item.district_name ?? "",
      province: item.province_name ?? "",
    }));

    // ปิด dropdown และล้าง query เพื่อไม่ให้ useEffect ยิงใหม่
    setZipOpen(false);
    setZipList([]);
    setZipQuery(""); // ล้าง query เพื่อหยุด useEffect แต่ไม่ blur ช่อง
  };

  // --- Billing ZIP search states ---
  const [billZipQuery, setBillZipQuery] = useState("");
  const [billZipList, setBillZipList] = useState([]);
  const [billZipOpen, setBillZipOpen] = useState(false);
  const [billZipLoading, setBillZipLoading] = useState(false);
  const billZipBoxRef = useRef(null);

  // ปิด dropdown เมื่อคลิกนอก (รวมของ billing)
  useEffect(() => {
    const onDocClick = (e) => {
      if (billZipBoxRef.current && !billZipBoxRef.current.contains(e.target)) {
        setBillZipOpen(false);
      }
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  useEffect(() => {
    if (billZipQuery === "") {
      setBillZipList([]);
      setBillZipOpen(false);
      return;
    }

    const delay = setTimeout(() => {
      setBillZipLoading(true);
      const current = billZipQuery;

      api
        .get(API_GETZIPCODE, { params: { zipcode: current } })
        .then((res) => {
          if (current !== billZipQuery) return;
          const rows = res?.data?.data ?? [];
          setBillZipList(rows);
          setBillZipOpen(true);
        })
        .catch(() => {
          if (current !== billZipQuery) return;
          setBillZipList([]);
          setBillZipOpen(false);
        })
        .finally(() => {
          if (current !== billZipQuery) return;
          setBillZipLoading(false);
        });
    }, 250);

    return () => clearTimeout(delay);
  }, [billZipQuery]);

  const onBillZipChange = (e) => {
    const v = e.target.value;
    setForm((s) => ({ ...s, billZipcode: v }));
    setBillZipQuery(v);
  };

  const onPickBillZip = (item) => {
    setForm((s) => ({
      ...s,
      billZipcode: String(item.zip_code ?? ""),
      billSubdistrict: item.subdistrict_name ?? "",
      billDistrict: item.district_name ?? "",
      billProvince: item.province_name ?? "",
    }));

    // ปิด dropdown โดยไม่ blur
    setBillZipOpen(false);
    setBillZipList([]);
    setBillZipQuery("");
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const customerTypeOptions = [
    {
      value: "บุคคลธรรมดา",
      label: "บุคคลธรรมดา",
    },
    {
      value: "นิติบุคคล",
      label: "นิติบุคคล",
    },
  ];

  const logoInputRef = useRef(null);

  const handleCancelAddCustomer = () => {
    navigate("/customer");
  };

  const [submitting, setSubmitting] = useState(false);

  //append เฉพาะกรณีมีค่า (กัน undefined/null)
  const appendStr = (fd, key, value) => {
    fd.append(key, String(value ?? ""));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    try {
      setSubmitting(true);

      const safeDigits = (v = "") => String(v).replace(/[^0-9]/g, "");
      const fd = new FormData();

      appendStr(fd, "TNID", tnId);
      appendStr(fd, "ORGID", orgId);

      const CMTYPE_LABEL =
        form.customerType === "บุคคลธรรมดา" ? "บุคคลธรรมดา" : "นิติบุคคล";
      appendStr(fd, "CMTYPE", CMTYPE_LABEL);

      if (logoFile) fd.append("file", logoFile);

      if (form.customerType === "บุคคลธรรมดา") {
        // --------- บุคคลธรรมดา ----------
        appendStr(fd, "CMNAME", form.cmName);
        appendStr(fd, "CMMOBILE", safeDigits(form.cmMobile));
        appendStr(fd, "CMADDR", form.cmAddr);
        appendStr(fd, "CMSUBDISTRICT", form.subdistrict);
        appendStr(fd, "CMDISTRICT", form.district);
        appendStr(fd, "CMPROVINCE", form.province);
        appendStr(fd, "CMZIPCODE", form.postalCode);

        appendStr(fd, "CMBILLNAMETH", "");
        appendStr(fd, "CMBILLNAMEEN", "");
        appendStr(fd, "CBID", "");
        appendStr(fd, "CMTAXID", "");
        appendStr(fd, "CMEMAIL", "");
        appendStr(fd, "CMADDR2", "");
        appendStr(fd, "CMBILLADDR", "");
        appendStr(fd, "CMBILLADDR2", "");
        appendStr(fd, "CMBILLSUBDISTRICT", "");
        appendStr(fd, "CMBILLDISTRICT", "");
        appendStr(fd, "CMBILLPROVINCE", "");
        appendStr(fd, "CMBILLZIPCODE", "");
        appendStr(fd, "CMBILLMOBILE", "");
        appendStr(fd, "CMBILLEMAIL", "");
        appendStr(fd, "CMCONTACTNAME", "");
        appendStr(fd, "CMCONTACTMOBILE", "");
      } else {
        // --------- นิติบุคคล ----------
        appendStr(fd, "CBID", form.businessType);
        appendStr(fd, "CMNAME", form.cmName);
        appendStr(fd, "CMTAXID", form.cmTaxId);
        appendStr(fd, "CMMOBILE", safeDigits(form.cmMobile));
        appendStr(fd, "CMEMAIL", form.cmEmail);
        appendStr(fd, "CMADDR", form.cmAddr);
        appendStr(fd, "CMADDR2", form.cmAddr2);
        appendStr(fd, "CMSUBDISTRICT", form.subdistrict);
        appendStr(fd, "CMDISTRICT", form.district);
        appendStr(fd, "CMPROVINCE", form.province);
        appendStr(fd, "CMZIPCODE", form.postalCode);

        appendStr(fd, "CMBILLNAMETH", form.cmBillNameTH);
        appendStr(fd, "CMBILLNAMEEN", form.cmBillNameEN);

        appendStr(fd, "CMBILLADDR", form.billAddr || form.cmAddr);
        appendStr(fd, "CMBILLADDR2", form.billAddr2 || form.cmAddr2);
        appendStr(
          fd,
          "CMBILLSUBDISTRICT",
          form.billSubdistrict || form.subdistrict
        );
        appendStr(fd, "CMBILLDISTRICT", form.billDistrict || form.district);
        appendStr(fd, "CMBILLPROVINCE", form.billProvince || form.province);
        appendStr(fd, "CMBILLZIPCODE", form.billZipcode || form.postalCode);
        appendStr(fd, "CMBILLMOBILE", safeDigits(form.billMobile));
        appendStr(fd, "CMBILLEMAIL", form.billEmail);

        appendStr(fd, "CMCONTACTNAME", form.contactName);
        appendStr(fd, "CMCONTACTMOBILE", safeDigits(form.contactMobile));
      }

      // ยิง API
      const res = await api.post(API_ADDCUSTOMER, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // ถ้ามี statustext จาก backend
      if ((res.data?.statustext || "").toUpperCase() !== "SUCCESS") {
        throw new Error(res.data?.message || "บันทึกลูกค้าไม่สำเร็จ");
      }

      await Swal.fire({
        icon: "success",
        title: "บันทึกสำเร็จ",
        showConfirmButton: false,
        timer: 1500,
      });

      navigate("/customer");
    } catch (err) {
      console.error("AddCustomer error:", err);
      await Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: err?.message || "บันทึกลูกค้าไม่สำเร็จ",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />
      {/* Page content */}
      <Container className="mt--7" fluid>
        <Row>
          <Col xs="12">
            <Card className="bg-secondary shadow">
              <CardHeader className="bg-white border-0">
                <Row className="align-items-center">
                  <Col xs="8">
                    <h6 className="mb-0">เพิ่มข้อมูลลูกค้า</h6>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                <Form autoComplete="off">
                  <h6 className="heading-small text-muted mb-4">
                    ข้อมูลพื้นฐาน
                  </h6>

                  <div className="pl-lg-4">
                    <Row>
                      <Col lg="12">
                        <FormGroup>
                          {customerTypeOptions.map((o) => {
                            const active = form.customerType === o.value;
                            return (
                              <Button
                                key={o.value}
                                size="sm"
                                outline={!active}
                                color={active ? "primary" : "secondary"}
                                className="mr-2 mb-2 rounded-pill"
                                onClick={() =>
                                  setForm((s) => ({
                                    ...s,
                                    customerType: o.value,
                                  }))
                                }
                                aria-pressed={active}
                              >
                                {o.label}
                              </Button>
                            );
                          })}
                          <Input
                            type="hidden"
                            name="customerType"
                            value={form.customerType}
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    {form.customerType === "บุคคลธรรมดา" && (
                      <>
                        <Row className="justify-content-center">
                          <Col lg="6" md="8" sm="10" className="mx-auto">
                            <FormGroup className="text-center">
                              <label
                                className="form-control-label d-block"
                                htmlFor="org-logo-input"
                              >
                                รูปโปรไฟล์
                              </label>

                              <div className="d-flex align-items-center justify-content-center w-100 mb-3">
                                <div
                                  className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
                                  style={{
                                    width: 100,
                                    height: 100,
                                    overflow: "hidden",
                                    border: "1px solid #e9ecef",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => logoInputRef.current?.click()}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) =>
                                    (e.key === "Enter" || e.key === " ") &&
                                    logoInputRef.current?.click()
                                  }
                                  aria-label="อัปโหลดโลโก้องค์กร"
                                  title="อัปโหลดโลโก้องค์กร"
                                >
                                  {logoPreview ? (
                                    <img
                                      alt="ตัวอย่างโลโก้องค์กร"
                                      src={logoPreview}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <span className="text-muted small">
                                      เลือกรูป
                                    </span>
                                  )}
                                </div>
                              </div>

                              <Input
                                innerRef={logoInputRef}
                                id="org-logo-input"
                                type="file"
                                accept="image/*"
                                onChange={onLogoChange}
                                className="d-none"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label required"
                                htmlFor="input-last-name"
                              >
                                ชื่อลูกค้า
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-cmname"
                                type="text"
                                name="cmName"
                                value={form.cmName}
                                onChange={onChange}
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label required"
                                htmlFor="input-city"
                              >
                                เบอร์โทร
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-cmmobile"
                                type="text"
                                name="cmMobile"
                                value={form.cmMobile}
                                onChange={(e) => {
                                  const onlyNums = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  setForm((s) => ({
                                    ...s,
                                    cmMobile: onlyNums,
                                  }));
                                }}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={10}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="12">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-city"
                              >
                                ที่อยู่
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-city"
                                type="text"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="3">
                            <FormGroup
                              style={{ position: "relative" }}
                              innerRef={zipBoxRef}
                            >
                              <label
                                className="form-control-label"
                                htmlFor="input-postal-code"
                              >
                                รหัสไปรษณีย์
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-postal-code"
                                type="text"
                                value={form.postalCode}
                                onChange={onZipChange}
                                onFocus={() =>
                                  zipList.length && setZipOpen(true)
                                }
                                autoComplete="new-password"
                                name="fakeZipcode"
                              />

                              {/* Dropdown */}
                              {zipOpen && (
                                <div
                                  className="dropdown-menu show w-100"
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    zIndex: 1050,
                                    maxHeight: 240,
                                    overflowY: "auto",
                                  }}
                                >
                                  {zipLoading && (
                                    <div className="dropdown-item text-muted small">
                                      กำลังค้นหา…
                                    </div>
                                  )}

                                  {!zipLoading && zipList.length === 0 && (
                                    <div className="dropdown-item text-muted small">
                                      ไม่พบข้อมูลรหัสนี้
                                    </div>
                                  )}

                                  {!zipLoading &&
                                    zipList.map((it, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        className="dropdown-item d-flex flex-column align-items-start"
                                        onClick={() => onPickZip(it)}
                                      >
                                        <div>
                                          <strong>{it.zip_code}</strong>
                                        </div>
                                        <div className="small text-muted">
                                          {it.subdistrict_name} •{" "}
                                          {it.district_name} •{" "}
                                          {it.province_name}
                                        </div>
                                      </button>
                                    ))}
                                </div>
                              )}
                            </FormGroup>
                          </Col>

                          <Col lg="3">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-subdistrict"
                              >
                                ตำบล/แขวง
                              </label>
                              <Input
                                disabled
                                className="form-control-alternative"
                                id="input-subdistrict"
                                type="text"
                                value={form.subdistrict}
                                onChange={(e) =>
                                  setForm((s) => ({
                                    ...s,
                                    subdistrict: e.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>

                          <Col lg="3">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-district"
                              >
                                อำเภอ/เขต
                              </label>
                              <Input
                                disabled
                                className="form-control-alternative"
                                id="input-district"
                                type="text"
                                value={form.district}
                                onChange={(e) =>
                                  setForm((s) => ({
                                    ...s,
                                    district: e.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>

                          <Col lg="3">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-province"
                              >
                                จังหวัด
                              </label>
                              <Input
                                disabled
                                className="form-control-alternative"
                                id="input-province"
                                type="text"
                                value={form.province}
                                onChange={(e) =>
                                  setForm((s) => ({
                                    ...s,
                                    province: e.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </>
                    )}
                    {form.customerType === "นิติบุคคล" && (
                      <>
                        <Row className="justify-content-center">
                          <Col lg="6" md="8" sm="10" className="mx-auto">
                            <FormGroup className="text-center">
                              <label
                                className="form-control-label d-block"
                                htmlFor="org-logo-input"
                              >
                                โลโก้องค์กร
                              </label>

                              <div className="d-flex align-items-center justify-content-center w-100 mb-3">
                                <div
                                  className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
                                  style={{
                                    width: 100,
                                    height: 100,
                                    overflow: "hidden",
                                    border: "1px solid #e9ecef",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => logoInputRef.current?.click()}
                                  role="button"
                                  tabIndex={0}
                                  onKeyDown={(e) =>
                                    (e.key === "Enter" || e.key === " ") &&
                                    logoInputRef.current?.click()
                                  }
                                  aria-label="อัปโหลดโลโก้องค์กร"
                                  title="อัปโหลดโลโก้องค์กร"
                                >
                                  {logoPreview ? (
                                    <img
                                      alt="ตัวอย่างโลโก้องค์กร"
                                      src={logoPreview}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <span className="text-muted small">
                                      เลือกรูป
                                    </span>
                                  )}
                                </div>
                              </div>

                              <Input
                                innerRef={logoInputRef}
                                id="org-logo-input"
                                type="file"
                                accept="image/*"
                                onChange={onLogoChange}
                                className="d-none"
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label d-block required"
                                htmlFor="input-business-type"
                              >
                                ประเภทธุรกิจ
                              </label>
                              <Input
                                className="form-control-alternative"
                                type="select"
                                id="input-business-type"
                                name="businessType"
                                value={form.businessType}
                                onChange={onChange}
                                required
                              >
                                <option value="">
                                  {loadingBusinessType
                                    ? "กำลังโหลด..."
                                    : "-- เลือก --"}
                                </option>
                                {businessTypeOptions.map((o) => (
                                  <option key={o.cbId} value={o.cbId}>
                                    {o.cbName}
                                  </option>
                                ))}
                              </Input>
                            </FormGroup>
                          </Col>

                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label required"
                                htmlFor="input-last-name"
                              >
                                ชื่อลูกค้า
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-cmname"
                                type="text"
                                name="cmName"
                                value={form.cmName}
                                onChange={onChange}
                                required
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label required"
                                htmlFor="input-city"
                              >
                                ที่อยู่
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-cmaddr"
                                type="text"
                                name="cmAddr"
                                value={form.cmAddr}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-country"
                              >
                                ที่อยู่เพิ่มเติม
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-cmaddr2"
                                type="text"
                                name="cmAddr2"
                                value={form.cmAddr2}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="3">
                            <FormGroup
                              style={{ position: "relative" }}
                              innerRef={zipBoxRef}
                            >
                              <label
                                className="form-control-label required"
                                htmlFor="input-postal-code"
                              >
                                รหัสไปรษณีย์
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-postal-code"
                                type="text"
                                value={form.postalCode}
                                onChange={onZipChange}
                                onFocus={() =>
                                  zipList.length && setZipOpen(true)
                                }
                                autoComplete="off"
                              />

                              {/* Dropdown */}
                              {zipOpen && (
                                <div
                                  className="dropdown-menu show w-100"
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    zIndex: 1050,
                                    maxHeight: 240,
                                    overflowY: "auto",
                                  }}
                                >
                                  {zipLoading && (
                                    <div className="dropdown-item text-muted small">
                                      กำลังค้นหา…
                                    </div>
                                  )}

                                  {!zipLoading && zipList.length === 0 && (
                                    <div className="dropdown-item text-muted small">
                                      ไม่พบข้อมูลรหัสนี้
                                    </div>
                                  )}

                                  {!zipLoading &&
                                    zipList.map((it, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        className="dropdown-item d-flex flex-column align-items-start"
                                        onClick={() => onPickZip(it)}
                                      >
                                        <div>
                                          <strong>{it.zip_code}</strong>
                                        </div>
                                        <div className="small text-muted">
                                          {it.subdistrict_name} •{" "}
                                          {it.district_name} •{" "}
                                          {it.province_name}
                                        </div>
                                      </button>
                                    ))}
                                </div>
                              )}
                            </FormGroup>
                          </Col>

                          <Col lg="3">
                            <FormGroup>
                              <label
                                className="form-control-label required"
                                htmlFor="input-subdistrict"
                              >
                                ตำบล/แขวง
                              </label>
                              <Input
                                disabled
                                className="form-control-alternative"
                                id="input-subdistrict"
                                type="text"
                                value={form.subdistrict}
                                onChange={(e) =>
                                  setForm((s) => ({
                                    ...s,
                                    subdistrict: e.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>

                          <Col lg="3">
                            <FormGroup>
                              <label
                                className="form-control-label required"
                                htmlFor="input-district"
                              >
                                อำเภอ/เขต
                              </label>
                              <Input
                                disabled
                                className="form-control-alternative"
                                id="input-district"
                                type="text"
                                value={form.district}
                                onChange={(e) =>
                                  setForm((s) => ({
                                    ...s,
                                    district: e.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>

                          <Col lg="3">
                            <FormGroup>
                              <label
                                className="form-control-label required"
                                htmlFor="input-province"
                              >
                                จังหวัด
                              </label>
                              <Input
                                disabled
                                className="form-control-alternative"
                                id="input-province"
                                type="text"
                                value={form.province}
                                onChange={(e) =>
                                  setForm((s) => ({
                                    ...s,
                                    province: e.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label required"
                                htmlFor="input-city"
                              >
                                เบอร์โทร
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-cmmobile"
                                type="text"
                                name="cmMobile"
                                value={form.cmMobile}
                                onChange={(e) => {
                                  const onlyNums = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  setForm((s) => ({
                                    ...s,
                                    cmMobile: onlyNums,
                                  }));
                                }}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={10}
                              />
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-city"
                              >
                                Email
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-cmemail"
                                type="email"
                                name="cmEmail"
                                value={form.cmEmail}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <hr className="my-4" />

                        <h6 className="heading-small text-muted mb-4">
                          ข้อมูลที่แสดงในใบกำกับภาษี
                        </h6>

                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-bill-name-th"
                              >
                                ชื่อบริษัท (ภาษาไทย)
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-bill-name-th"
                                type="text"
                                name="cmBillNameTH"
                                value={form.cmBillNameTH}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>

                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-bill-name-en"
                              >
                                ชื่อบริษัท (ภาษาอังกฤษ)
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-bill-name-en"
                                type="text"
                                name="cmBillNameEN"
                                value={form.cmBillNameEN}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="12">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-last-name"
                              >
                                เลขประจำตัวผู้เสียภาษี
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-taxid"
                                type="text"
                                name="cmTaxId"
                                value={form.cmTaxId}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-city"
                              >
                                ที่อยู่
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-bill-addr"
                                type="text"
                                name="billAddr"
                                value={form.billAddr}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-country"
                              >
                                ที่อยู่เพิ่มเติม
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-bill-addr2"
                                type="text"
                                name="billAddr2"
                                value={form.billAddr2}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="3">
                            <FormGroup
                              style={{ position: "relative" }}
                              innerRef={billZipBoxRef}
                            >
                              <label
                                className="form-control-label"
                                htmlFor="input-bill-zipcode"
                              >
                                รหัสไปรษณีย์
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-bill-zipcode"
                                type="text"
                                value={form.billZipcode}
                                onChange={onBillZipChange}
                                onFocus={() =>
                                  billZipList.length && setBillZipOpen(true)
                                }
                                autoComplete="new-password"
                                name="fakeBillZipcode"
                                inputMode="numeric"
                                pattern="[0-9]*"
                              />

                              {billZipOpen && (
                                <div
                                  className="dropdown-menu show w-100"
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    zIndex: 1050,
                                    maxHeight: 240,
                                    overflowY: "auto",
                                  }}
                                >
                                  {billZipLoading && (
                                    <div className="dropdown-item text-muted small">
                                      กำลังค้นหา…
                                    </div>
                                  )}

                                  {!billZipLoading &&
                                    billZipList.length === 0 && (
                                      <div className="dropdown-item text-muted small">
                                        ไม่พบข้อมูลรหัสนี้
                                      </div>
                                    )}

                                  {!billZipLoading &&
                                    billZipList.map((it, idx) => (
                                      <button
                                        key={idx}
                                        type="button"
                                        className="dropdown-item d-flex flex-column align-items-start"
                                        onClick={() => onPickBillZip(it)}
                                      >
                                        <div>
                                          <strong>{it.zip_code}</strong>
                                        </div>
                                        <div className="small text-muted">
                                          {it.subdistrict_name} •{" "}
                                          {it.district_name} •{" "}
                                          {it.province_name}
                                        </div>
                                      </button>
                                    ))}
                                </div>
                              )}
                            </FormGroup>
                          </Col>

                          <Col lg="3">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-bill-subdistrict"
                              >
                                ตำบล/แขวง
                              </label>
                              <Input
                                disabled
                                className="form-control-alternative"
                                id="input-bill-subdistrict"
                                type="text"
                                value={form.billSubdistrict}
                                onChange={(e) =>
                                  setForm((s) => ({
                                    ...s,
                                    billSubdistrict: e.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>

                          <Col lg="3">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-bill-district"
                              >
                                อำเภอ/เขต
                              </label>
                              <Input
                                disabled
                                className="form-control-alternative"
                                id="input-bill-district"
                                type="text"
                                value={form.billDistrict}
                                onChange={(e) =>
                                  setForm((s) => ({
                                    ...s,
                                    billDistrict: e.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>

                          <Col lg="3">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-bill-province"
                              >
                                จังหวัด
                              </label>
                              <Input
                                disabled
                                className="form-control-alternative"
                                id="input-bill-province"
                                type="text"
                                value={form.billProvince}
                                onChange={(e) =>
                                  setForm((s) => ({
                                    ...s,
                                    billProvince: e.target.value,
                                  }))
                                }
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-city"
                              >
                                เบอร์โทร
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-bill-mobile"
                                type="text"
                                name="billMobile"
                                value={form.billMobile}
                                onChange={(e) => {
                                  const onlyNums = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  setForm((s) => ({
                                    ...s,
                                    billMobile: onlyNums,
                                  }));
                                }}
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={10}
                              />
                            </FormGroup>
                          </Col>

                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-city"
                              >
                                Email
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-bill-email"
                                type="email"
                                name="billEmail"
                                value={form.billEmail}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <hr className="my-4" />

                        <h6 className="heading-small text-muted mb-4">
                          ข้อมูลผู้ติดต่อ
                        </h6>

                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-last-name"
                              >
                                ผู้ติดต่อ
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-contact-name"
                                type="text"
                                name="contactName"
                                value={form.contactName}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>

                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-last-name"
                              >
                                เบอร์โทร
                              </label>
                              <Input
                                className="form-control-alternative"
                                id="input-contact-mobile"
                                type="text"
                                name="contactMobile"
                                value={form.contactMobile}
                                onChange={onChange}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </>
                    )}
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ModalFooter>
        <Button color="secondary" onClick={handleCancelAddCustomer}>
          ยกเลิก
        </Button>
        <Button color="primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "กำลังบันทึก..." : "บันทึก"}
        </Button>
      </ModalFooter>
    </>
  );
};

export default AddCustomer;
